import { EmptyState, FlexColumn, Loading, MaterialIcon, useLoading, useModal } from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { useEffect, useState } from 'react';
import { TABLE_COLUMNS_NAME } from '../../../constants/maintenanceTypes';
import { useFeedback } from '../../../hooks/useFeedback';
import { useStyles } from '../../../hooks/useStyles';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import { MaintenanceType } from '../../../types/maintenances';
import { StyledPaginatedTable } from '../../shared/StyledPaginatedTable';
import StyledTooltip from '../../ui/StyledTooltip';
import useMaintenanceTypes from '../hooks/useMaintenanceTypes';
import MaintenanceTypeModal from './MaintenanceTypeModal';

function MaintenanceTypeList() {
  const { theme, css, classes } = useStyles();
  const [maintenanceTypes, setMaintenanceTypes] = useState<MaintenanceType[]>();
  const { page, onChangePage, loadFleetMaintenanceTypes, reload, search, onReload } = useMaintenanceTypes();
  const [totalItems, setTotalItems] = useState(0);
  const { loading, stopLoading, startLoading } = useLoading(true);
  const { showFailFeedback } = useFeedback();
  const [selectedMaintenanceType, setSelectedMaintenanceType] = useState<MaintenanceType>();
  const { openModal, closeModal, showModal } = useModal();

  useUpdateEffect(() => {
    onChangePage(0);
  }, [search, reload]);

  useEffect(() => {
    if (page === 0) {
      onChangePage(1);
      return;
    }
    const loadData = async () => {
      startLoading();
      await loadMaintenanceTypes();
      stopLoading();
    };

    loadData();
  }, [page]);

  const loadMaintenanceTypes = async () => {
    const { types, error, total } = await loadFleetMaintenanceTypes();
    if (error) {
      showFailFeedback(error);
      setMaintenanceTypes(undefined);
      setTotalItems(0);
    } else {
      setMaintenanceTypes(types);
      setTotalItems(total ?? 0);
    }
  };

  const onOpenDetailModal = (maintenanceType: MaintenanceType) => {
    setSelectedMaintenanceType(maintenanceType);
    openModal();
  };

  return (
    <FlexColumn classNames={`${classes.flexFill} ${css({ overflow: 'hidden' })}`}>
      {!maintenanceTypes && !loading && <EmptyState title="Todavía no cuentas con ningún tipo de mantenimiento." />}
      {!maintenanceTypes && loading && <Loading />}
      {maintenanceTypes && (
        <>
          {!!maintenanceTypes?.length && (
            <StyledPaginatedTable
              columns={TABLE_COLUMNS_NAME}
              data={maintenanceTypes.map((maintenanceType) => {
                return {
                  items: [
                    maintenanceType.value,
                    maintenanceType.slug,
                    <StyledTooltip
                      key={`edit-${maintenanceType._id}`}
                      content="Editar"
                      showArrow={false}
                    >
                      <Button
                        size="mini"
                        shape="square"
                        onClick={() => onOpenDetailModal(maintenanceType)}
                        kind="tertiary"
                      >
                        <MaterialIcon
                          name="edit"
                          size={theme.sizing.scale700}
                        />
                      </Button>
                    </StyledTooltip>,
                  ],
                };
              })}
              totalItems={totalItems}
              page={page}
              onPageChange={onChangePage}
              loading={loading}
            />
          )}
          {!maintenanceTypes.length && (
            <EmptyState
              title="No se encontraron resultados"
              description="Intenta con otra busqueda"
            />
          )}
        </>
      )}
      <MaintenanceTypeModal
        isOpen={showModal}
        onClose={(update) => {
          if (update) onReload();
          closeModal();
        }}
        selectedMaintenanceType={selectedMaintenanceType}
      />
    </FlexColumn>
  );
}

export default MaintenanceTypeList;
